import api from './api';

/**
 * Servicio para gestión de futbolistas PostgreSQL
 * 
 * Conecta el frontend con las APIs de futbolistas
 * Permite a tutores y admins gestionar futbolistas
 */

const futbolistaService = {
  /**
   * Obtener futbolistas (del tutor actual o todos si es admin)
   * @returns {Promise<Object>} Lista de futbolistas
   */
  async getFutbolistas() {
    try {
      const response = await api.get('/postgres/futbolistas');
      console.log('🔍 futbolistaService.getFutbolistas response:', response.data);

      // El backend devuelve { success: true, count: X, data: [...] }
      // Extraer el array de futbolistas del campo 'data'
      const futbolistasData = response.data?.data || [];

      // Asegurar que siempre devolvemos un array
      return Array.isArray(futbolistasData) ? futbolistasData : [];
    } catch (error) {
      console.error('Error al obtener futbolistas:', error);
      // En caso de error, devolver array vacío en lugar de lanzar error
      return [];
    }
  },

  /**
   * Buscar futbolistas por nombre (con límite de resultados)
   * @param {string} searchQuery - Término de búsqueda
   * @returns {Promise<Array>} Lista de futbolistas filtrados
   */
  async getFutbolistasBySearch(searchQuery) {
    try {
      const response = await api.get(`/postgres/futbolistas?search=${encodeURIComponent(searchQuery)}`);
      console.log('🔍 futbolistaService.getFutbolistasBySearch response:', response.data);

      // El backend devuelve { success: true, count: X, data: [...] }
      const futbolistasData = response.data?.data || [];

      // Asegurar que siempre devolvemos un array limitado
      const limitedResults = Array.isArray(futbolistasData) ? futbolistasData.slice(0, 30) : [];
      return limitedResults;
    } catch (error) {
      console.error('Error al buscar futbolistas:', error);
      return [];
    }
  },

  /**
   * Buscar futbolistas (alias para compatibilidad)
   * @param {string} searchQuery - Término de búsqueda
   * @returns {Promise<Object>} Respuesta con lista de futbolistas
   */
  async searchFutbolistas(searchQuery) {
    try {
      const futbolistas = await this.getFutbolistasBySearch(searchQuery);
      return {
        success: true,
        data: futbolistas
      };
    } catch (error) {
      console.error('Error al buscar futbolistas:', error);
      return {
        success: false,
        data: []
      };
    }
  },

  /**
   * Obtener futbolista por ID
   * @param {number} id - ID del futbolista
   * @returns {Promise<Object>} Futbolista
   */
  async getFutbolista(id) {
    try {
      const response = await api.get(`/postgres/futbolistas/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener futbolista:', error);
      throw error;
    }
  },

  /**
   * Obtener credenciales de acceso del futbolista
   * @param {number} id - ID del futbolista
   * @returns {Promise<Object>} Credenciales del futbolista
   */
  async getCredenciales(id) {
    try {
      const response = await api.get(`/postgres/futbolistas/${id}/credenciales`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener credenciales:', error);
      throw error;
    }
  },

  /**
   * Crear nuevo futbolista
   * @param {Object} futbolistaData - Datos del futbolista
   * @returns {Promise<Object>} Futbolista creado
   */
  async createFutbolista(futbolistaData) {
    try {
      const response = await api.post('/postgres/futbolistas', futbolistaData);
      return response.data;
    } catch (error) {
      console.error('Error al crear futbolista:', error);
      throw error;
    }
  },

  /**
   * Actualizar futbolista
   * @param {number} id - ID del futbolista
   * @param {Object} futbolistaData - Datos actualizados
   * @returns {Promise<Object>} Futbolista actualizado
   */
  async updateFutbolista(id, futbolistaData) {
    try {
      const response = await api.put(`/postgres/futbolistas/${id}`, futbolistaData);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar futbolista:', error);
      throw error;
    }
  },

  /**
   * Eliminar futbolista
   * @param {number} id - ID del futbolista
   * @returns {Promise<Object>} Confirmación
   */
  async deleteFutbolista(id) {
    try {
      const response = await api.delete(`/postgres/futbolistas/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar futbolista:', error);
      throw error;
    }
  },

  /**
   * Obtener datos auxiliares para formularios
   * @returns {Promise<Object>} Datos auxiliares (clubes, divisiones, etc.)
   */
  async getAuxiliarData() {
    try {
      // Obtener datos reales de la API usando la ruta combinada
      const auxiliarRes = await api.get('/postgres/auxiliar/all');

      return auxiliarRes.data?.data || {
        temporadas: [
          { id: 1, nombre: 'Temporada Anterior', ano_inicio: 2024, ano_fin: 2025 },
          { id: 2, nombre: 'Temporada Actual', ano_inicio: 2025, ano_fin: 2026 }
        ],
        clubes: [],
        divisiones: [
          // Comunidad Valenciana (orden del cliente)
          { id: 35, competicion: 'Autonómica', federacion: 'Comunidad Valenciana', federado: true, orden: 1 },
          { id: 34, competicion: 'Preferente', federacion: 'Comunidad Valenciana', federado: true, orden: 2 },
          { id: 33, competicion: 'Primera', federacion: 'Comunidad Valenciana', federado: true, orden: 3 },
          { id: 32, competicion: 'Segunda', federacion: 'Comunidad Valenciana', federado: true, orden: 4 },
          { id: 31, competicion: 'Tercera', federacion: 'Comunidad Valenciana', federado: true, orden: 5 },
          { id: 29, competicion: 'No federado (juego en un equipo)', federacion: 'Comunidad Valenciana', federado: false, orden: 6 },
          { id: 30, competicion: 'No federado (sin equipo)', federacion: 'Comunidad Valenciana', federado: false, orden: 7 },
          // Murcia (orden del cliente)
          { id: 40, competicion: 'Autonómica', federacion: 'Murcia', federado: true, orden: 1 },
          { id: 41, competicion: 'Superliga', federacion: 'Murcia', federado: true, orden: 2 },
          { id: 39, competicion: 'Primera', federacion: 'Murcia', federado: true, orden: 3 },
          { id: 36, competicion: 'Segunda', federacion: 'Murcia', federado: true, orden: 4 },
          { id: 37, competicion: 'No federado (juego en un equipo)', federacion: 'Murcia', federado: false, orden: 5 },
          { id: 38, competicion: 'No federado (sin equipo)', federacion: 'Murcia', federado: false, orden: 6 }
        ],
        demarcaciones: [
          { id: 1, posicion: 'Portero' },
          { id: 2, posicion: 'Defensa central/Lateral' },
          { id: 3, posicion: 'Medio/Extremo' },
          { id: 4, posicion: 'Delantero/Mediapunta' }
        ],
        sedes: [
          { id: 1, nombre: 'Complejo Deportivo Algorfa', direccion: 'Calle del Deporte, 15', poblacion: 'Algorfa' },
          { id: 2, nombre: 'Ciudad Deportiva Alicante', direccion: 'Avenida de los Deportes, 42', poblacion: 'Alicante' }
        ]
      };
    } catch (error) {
      console.error('Error al obtener datos auxiliares:', error);
      // Devolver datos por defecto en caso de error
      return {
        temporadas: [
          { id: 1, nombre: 'Temporada Anterior', ano_inicio: 2024, ano_fin: 2025 },
          { id: 2, nombre: 'Temporada Actual', ano_inicio: 2025, ano_fin: 2026 }
        ],
        clubes: [],
        divisiones: [
          // Comunidad Valenciana (orden del cliente)
          { id: 35, competicion: 'Autonómica', federacion: 'Comunidad Valenciana', federado: true, orden: 1 },
          { id: 34, competicion: 'Preferente', federacion: 'Comunidad Valenciana', federado: true, orden: 2 },
          { id: 33, competicion: 'Primera', federacion: 'Comunidad Valenciana', federado: true, orden: 3 },
          { id: 32, competicion: 'Segunda', federacion: 'Comunidad Valenciana', federado: true, orden: 4 },
          { id: 31, competicion: 'Tercera', federacion: 'Comunidad Valenciana', federado: true, orden: 5 },
          { id: 29, competicion: 'No federado (juego en un equipo)', federacion: 'Comunidad Valenciana', federado: false, orden: 6 },
          { id: 30, competicion: 'No federado (sin equipo)', federacion: 'Comunidad Valenciana', federado: false, orden: 7 },
          // Murcia (orden del cliente)
          { id: 40, competicion: 'Autonómica', federacion: 'Murcia', federado: true, orden: 1 },
          { id: 41, competicion: 'Superliga', federacion: 'Murcia', federado: true, orden: 2 },
          { id: 39, competicion: 'Primera', federacion: 'Murcia', federado: true, orden: 3 },
          { id: 36, competicion: 'Segunda', federacion: 'Murcia', federado: true, orden: 4 },
          { id: 37, competicion: 'No federado (juego en un equipo)', federacion: 'Murcia', federado: false, orden: 5 },
          { id: 38, competicion: 'No federado (sin equipo)', federacion: 'Murcia', federado: false, orden: 6 }
        ],
        demarcaciones: [
          { id: 1, posicion: 'Portero' },
          { id: 2, posicion: 'Defensa central/Lateral' },
          { id: 3, posicion: 'Medio/Extremo' },
          { id: 4, posicion: 'Delantero/Mediapunta' }
        ],
        sedes: [
          { id: 1, nombre: 'Complejo Deportivo Algorfa', direccion: 'Calle del Deporte, 15', poblacion: 'Algorfa' },
          { id: 2, nombre: 'Ciudad Deportiva Alicante', direccion: 'Avenida de los Deportes, 42', poblacion: 'Alicante' }
        ]
      };
    }
  },

  /**
   * Obtener estadísticas completas del tutor
   * @returns {Promise<Object>} Estadísticas completas del tutor
   */
  async getTutorStats() {
    try {
      const response = await api.get('/postgres/futbolistas/tutor/stats');

      if (response.data && response.data.success) {
        return response.data.data;
      }

      return {
        futbolistasRegistrados: 0,
        futbolistasTemporadaActual: 0,
        inscripcionesActivas: 0,
        torneosActivos: 0,
        temporadaActual: 'N/A'
      };
    } catch (error) {
      console.error('Error al obtener estadísticas del tutor:', error);
      return {
        futbolistasRegistrados: 0,
        futbolistasTemporadaActual: 0,
        inscripcionesActivas: 0,
        torneosActivos: 0,
        temporadaActual: 'N/A'
      };
    }
  },

  // Obtener ProPoints de un futbolista específico (para tutores)
  async getFutbolistaProPoints(futbolistaId, torneoId = null) {
    const params = torneoId ? { torneoId } : {};
    const response = await api.get(`/postgres/futbolistas/${futbolistaId}/propoints`, { params });
    return response.data;
  },

  // Obtener logros de un futbolista específico (para tutores)
  async getFutbolistaLogros(futbolistaId, torneoId = null) {
    const params = torneoId ? { torneoId } : {};
    const response = await api.get(`/postgres/futbolistas/${futbolistaId}/logros`, { params });
    return response.data;
  },

  // Obtener nivel de un futbolista específico (para tutores)
  async getFutbolistaNivel(futbolistaId, torneoId = null) {
    const params = torneoId ? { torneoId } : {};
    const response = await api.get(`/postgres/futbolistas/${futbolistaId}/nivel`, { params });
    return response.data;
  },

  // Obtener torneos donde un futbolista tiene inscripción, agrupados por temporada
  async getFutbolistaTorneos(futbolistaId) {
    const response = await api.get(`/postgres/futbolistas/${futbolistaId}/torneos`);
    return response.data;
  },

  // Obtener horarios de partidos para futbolistas del tutor en una jornada específica
  async getFutbolistasHorariosJornada(jornadaId) {
    const response = await api.get(`/postgres/futbolistas/horarios-jornada/${jornadaId}`);
    return response.data;
  },

  // Obtener horario de partido para un futbolista específico en una jornada
  async getFutbolistaHorarioJornada(futbolistaId, jornadaId) {
    const response = await api.get(`/postgres/futbolistas/${futbolistaId}/horario-jornada/${jornadaId}`);
    return response.data;
  },

  // Renovar futbolista para la temporada activa
  async renovarTemporada(futbolistaId, datosOpcionales = {}) {
    const response = await api.post(`/postgres/futbolistas/${futbolistaId}/renovar`, datosOpcionales);
    return response.data;
  },

  // Alias para compatibilidad con componentes que usan 'delete'
  delete: function(id) {
    return this.deleteFutbolista(id);
  }
};

export default futbolistaService;
